import React, { useRef } from 'react';
import { Bar } from 'react-chartjs-2';
import { useMainPerformanceSalesComparison, SectionProps } from '../../hooks/useMainPerformanceSections';
import { useTheme } from '../../contexts/ThemeContext';
import { getChartJsOptions, getChartColors, getChartBackgroundColor } from '../../utils/themeHelpers';
import LoadingSpinner from '../ui/LoadingSpinner';
import ErrorMessage from '../ui/ErrorMessage';
import EmptyState from '../ui/EmptyState';
import ChartControls from '../ui/ChartControls';
import useChartGuide from '../../hooks/useChartGuide';
import { CHART_GUIDES } from '../../constants/chartGuides';
import { useFavoriteToggle } from '../../hooks/useFavoriteToggle';

interface MonthlySalesComparisonProps extends SectionProps {
  branchId: number;
  timeRange?: string;
}

const MonthlySalesComparison: React.FC<MonthlySalesComparisonProps> = ({
  branchId,
  timeRange = 'last6Months',
  onError,
  showLoading = true,
  compact = false
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const { currentTheme } = useTheme();
  const { openGuide } = useChartGuide();
  const { handleFavoriteToggle, isFavorited } = useFavoriteToggle();
  const { data: comparisonData, isLoading, isError, error, refetch } = useMainPerformanceSalesComparison(branchId, timeRange);
  
  // Component metadata
  const componentId = 'monthly-sales-comparison';
  const componentTitle = 'مقایسه فروش ماهانه';

  // Handle error callback
  React.useEffect(() => {
    if (isError && error && onError) {
      onError(error as Error);
    }
  }, [isError, error, onError]);

  // Helper function to format numbers for display
  const formatKpiValue = (value: number | string, type: 'currency' | 'percentage' | 'number' = 'number'): string => {
    if (typeof value === 'string') return value;
    if (!value && value !== 0) return '0';
    
    switch (type) {
      case 'currency':
        return new Intl.NumberFormat('fa-IR').format(Math.round(value));
      case 'percentage':
        return `${value != null ? value.toFixed(1) : '0'}%`;
      default:
        return new Intl.NumberFormat('fa-IR').format(Math.round(value));
    }
  };

  if (isLoading && showLoading) {
    return (
      <div className={`${compact ? 'col-span-1' : 'col-span-1'} animate-enter animate-delay-3`}>
        <div className="card-base card-padding">
          <div className="flex flex-col items-center justify-center h-64">
            <LoadingSpinner
              variant="minimal"
              size="lg"
              message="در حال بارگذاری مقایسه فروش…"
            />
          </div>
        </div>
      </div>
    );
  }

  if (isError && error) {
    return (
      <div className={`${compact ? 'col-span-1' : 'col-span-1'}`}>
        <div className="card-base card-padding">
          <ErrorMessage 
            message="خطا در دریافت اطلاعات مقایسه فروش"
            onRetry={() => refetch()}
          />
        </div>
      </div>
    );
  }

  if (!comparisonData?.sales) {
    return (
      <div className={`${compact ? 'col-span-1' : 'col-span-1'}`}>
        <div className="card-base card-padding">
          <EmptyState />
        </div>
      </div>
    );
  }

  const sales = comparisonData.sales;

  return (
    <div ref={chartRef} className={`${compact ? 'col-span-1' : 'col-span-1'} card-base card-padding animate-enter animate-delay-3`}>
      <ChartControls
        componentId={componentId}
        componentTitle={componentTitle}
        componentType="chart"
        dashboardSource="mainPerformance"
        onInfo={() => openGuide(CHART_GUIDES.monthlySalesComparison)}
        onFavorite={() => handleFavoriteToggle({
          componentId,
          componentTitle,
          dashboardSource: 'mainPerformance',
          componentType: 'chart',
          description: 'مقایسه فروش ماهیانه بین شعبه، برند و کل',
          props: { branchId, timeRange, compact },
          gridLayout: {
            containerClass: 'grid grid-cols-1 lg:grid-cols-2 gap-6',
            companions: ['customer-satisfaction-comparison']
          }
        })}
        isFavorited={isFavorited(componentId)}
        chartRef={chartRef}
        className="mb-4"
      />

      <div  style={{ height: 400, width: '100%' }} role="img" aria-label="نمودار مقایسه فروش بین شعبه فعلی، میانگین برند و میانگین کل">
      <h3 className="title-card">{componentTitle}</h3>

        <Bar 
          data={{
            labels: ['شعبه فعلی', 'میانگین برند'],
            datasets: [
              {
                label: 'مبلغ فروش',
                data: [
                  sales.branch || 0,
                  sales.brand || 0,
                ],
                backgroundColor: getChartBackgroundColor(0, currentTheme, 0.8),
                borderColor: getChartColors(currentTheme)[0],
                borderWidth: 2,
                borderRadius: 6,
              }
            ]
          }}
          options={{
            ...getChartJsOptions(currentTheme),
            plugins: {
              ...getChartJsOptions(currentTheme).plugins,
              legend: { display: false },
              tooltip: {
                ...getChartJsOptions(currentTheme).plugins?.tooltip,
                callbacks: {
                  ...(getChartJsOptions(currentTheme).plugins?.tooltip?.callbacks || {}),
                  label: function(context: any) {
                    const label = 'مبلغ فروش';
                    const value = `${formatKpiValue(context.parsed.y, 'currency')} ${comparisonData?.currency?.currency_symbol || 'تومان'}`;
                    const separator = '\u00A0:\u00A0';
                    return `${label}${separator}${value}`;
                  }
                }
              }
            },
            scales: {
              ...getChartJsOptions(currentTheme).scales,
              y: {
                ...getChartJsOptions(currentTheme).scales?.y,
                beginAtZero: true,
                ticks: {
                  ...getChartJsOptions(currentTheme).scales?.y?.ticks,
                  callback: function(value: any) {
                    return formatKpiValue(value, 'currency');
                  }
                }
              }
            }
          }}
        />
      </div>
    </div>
  );
};

export default MonthlySalesComparison;